/**
 * The document actions on INV-02: void, duplicate and remind.
 *
 * Each one is a plain call to the contract route through
 * `features/invoicing/api.ts`, with an `Idempotency-Key` minted once per user
 * intent and reused if that same intent is retried after a transport failure —
 * so a retry cannot double-post. Each shows the service's own refusal instead of
 * an interpretation of it: the void in particular keeps `PAYMENT_IN_FLIGHT` on
 * screen, because "a payment is in flight" is the refusal a person most needs to
 * read rather than a generic failure.
 *
 * Controls require their exact scope from a loaded API effective-access reply.
 * The server still authorizes every request; revoked or unknown access hides
 * controls and prevents confirmation of an already-open dialog.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ApiErrorInfo } from "@/lib/api";
import { idempotencyKey } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Field } from "@/components/ui/input";
import { Textarea } from "@/components/ui/form-controls";
import { documentStatusPresentation } from "@/features/invoicing/status";
import { duplicateInvoice, remindInvoice, voidInvoice } from "@/features/invoicing/api";
import type { Invoice } from "@/features/invoicing/types";
import { DETAIL_COPY } from "./copy";
import { RefusalNotice } from "./refusal";
import { useInvoicingScope } from "@/features/invoicing/invoices-list/use-invoicing-scope";


function Receipt({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      className="mt-3 rounded-md border border-success-border bg-success-soft px-3.5 py-2.5 text-sm text-success"
    >
      {children}
    </p>
  );
}

const isPaymentInFlight = (error: ApiErrorInfo): boolean => error.reasonCode === "PAYMENT_IN_FLIGHT";

export function InvoiceActions({
  invoice,
  onChanged,
}: {
  invoice: Invoice;
  onChanged: () => void;
}) {
  const canVoid = useInvoicingScope("invoice:void");
  const canDuplicate = useInvoicingScope("invoice:create");
  const canRemind = useInvoicingScope("invoice:remind");

  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"void" | "duplicate" | "remind" | null>(null);
  const [refusal, setRefusal] = useState<{ error: ApiErrorInfo; action: "void" | "duplicate" | "remind" } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [voidReceipt, setVoidReceipt] = useState<string | null>(null);
  const [duplicateReceipt, setDuplicateReceipt] = useState<string | null>(null);
  const [remindReceipt, setRemindReceipt] = useState<string | null>(null);

  const voidKey = useRef<string | null>(null);
  const duplicateKey = useRef<string | null>(null);
  const remindKey = useRef<string | null>(null);

  useEffect(() => {
    if (!canVoid) {
      setVoidOpen(false);
      setReason("");
      voidKey.current = null;
    }
  }, [canVoid]);

  async function confirmVoid() {
    if (!canVoid || busy !== null) return;
    setFormError(null);
    setRefusal(null);
    if (reason.trim() === "") {
      setFormError(DETAIL_COPY.voidReasonRequired);
      return;
    }
    setBusy("void");
    if (!voidKey.current) voidKey.current = idempotencyKey("invoicing_invoice_void");
    const result = await voidInvoice(
      invoice.invoiceId,
      { reason: reason.trim() },
      { idempotencyKey: voidKey.current, ifMatch: invoice.version },
    );
    setBusy(null);
    if (!result.ok) {
      setRefusal({ error: result.error, action: "void" });
      return;
    }
    voidKey.current = null;
    setVoidReceipt(documentStatusPresentation(result.data.documentStatus).label);
    setReason("");
    setVoidOpen(false);
    onChanged();
  }

  async function duplicate() {
    if (!canDuplicate || busy !== null) return;
    setRefusal(null);
    setBusy("duplicate");
    if (!duplicateKey.current) duplicateKey.current = idempotencyKey("invoicing_invoice_duplicate");
    const result = await duplicateInvoice(invoice.invoiceId, { idempotencyKey: duplicateKey.current });
    setBusy(null);
    if (!result.ok) {
      setRefusal({ error: result.error, action: "duplicate" });
      return;
    }
    duplicateKey.current = null;
    setDuplicateReceipt(result.data.invoiceId);
  }

  async function remind() {
    if (!canRemind || busy !== null) return;
    setRefusal(null);
    setBusy("remind");
    if (!remindKey.current) remindKey.current = idempotencyKey("invoicing_invoice_remind");
    const result = await remindInvoice(
      invoice.invoiceId,
      { channel: "EMAIL" },
      { idempotencyKey: remindKey.current },
    );
    setBusy(null);
    if (!result.ok) {
      setRefusal({ error: result.error, action: "remind" });
      return;
    }
    remindKey.current = null;
    setRemindReceipt(DETAIL_COPY.remindReceipt(result.data.acceptedAt, result.data.channel));
  }

  if (!canVoid && !canDuplicate && !canRemind) return null;

  return (
    <section aria-labelledby="invoice-actions-heading" data-testid="invoice-actions" className="mb-4 rounded-lg border border-line bg-surface px-4 py-4">
      <h2 id="invoice-actions-heading" className="text-sm font-bold text-ink">
        {DETAIL_COPY.actionsHeading}
      </h2>

      <div className="mt-3 flex flex-col gap-3">
        {canVoid && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <Button variant="secondary" size="sm" onClick={() => setVoidOpen(true)} disabled={busy !== null} data-testid="invoice-void">
            {DETAIL_COPY.voidCta}
          </Button>
          <p className="max-w-[520px] text-xs leading-relaxed text-muted">{DETAIL_COPY.voidHint}</p>
        </div>
        )}
        {voidReceipt && <Receipt>{DETAIL_COPY.voidReceipt(voidReceipt)}</Receipt>}
        {/* Shown inline only while the void dialog is closed, so the same
            refusal is never rendered twice at once. */}
        {refusal?.action === "void" && !voidOpen && (
          <RefusalNotice
            error={refusal.error}
            title={isPaymentInFlight(refusal.error) ? DETAIL_COPY.voidRefusedTitle : DETAIL_COPY.refusedTitle}
          />
        )}

        {canDuplicate && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            loading={busy === "duplicate"}
            onClick={() => void duplicate()}
            data-testid="invoice-duplicate"
          >
            {DETAIL_COPY.duplicateCta}
          </Button>
          <p className="max-w-[520px] text-xs leading-relaxed text-muted">{DETAIL_COPY.duplicateHint}</p>
        </div>
        )}
        {duplicateReceipt && (
          <Receipt>
            {DETAIL_COPY.duplicateReceipt(duplicateReceipt)}{" "}
            <Link
              to={`/stablecoins/invoices/view?id=${encodeURIComponent(duplicateReceipt)}`}
              className="font-semibold underline"
            >
              {DETAIL_COPY.duplicateOpenLabel}
            </Link>
          </Receipt>
        )}
        {refusal?.action === "duplicate" && (
          <RefusalNotice error={refusal.error} title={DETAIL_COPY.refusedTitle} />
        )}

        {canRemind && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            loading={busy === "remind"}
            onClick={() => void remind()}
            data-testid="invoice-remind"
          >
            {DETAIL_COPY.remindCta}
          </Button>
          <p className="max-w-[520px] text-xs leading-relaxed text-muted">{DETAIL_COPY.remindHint}</p>
        </div>
        )}
        {remindReceipt && <Receipt>{remindReceipt}</Receipt>}
        {refusal?.action === "remind" && (
          <RefusalNotice error={refusal.error} title={DETAIL_COPY.refusedTitle} />
        )}
      </div>

      <Modal
        open={voidOpen && canVoid}
        onOpenChange={(next) => {
          setVoidOpen(next);
          if (!next) {
            setFormError(null);
            setRefusal((prev) => (prev?.action === "void" ? null : prev));
          }
        }}
        title={DETAIL_COPY.voidTitle}
        description={DETAIL_COPY.voidBody}
        footer={
          <>
            <Button
              variant="ghost"
              size="md"
              disabled={busy === "void"}
              onClick={() => {
                setVoidOpen(false);
                setFormError(null);
              }}
            >
              {DETAIL_COPY.voidCancel}
            </Button>
            <Button
              variant="destructive"
              size="md"
              loading={busy === "void"}
              onClick={() => void confirmVoid()}
              data-testid="invoice-void-confirm"
            >
              {DETAIL_COPY.voidConfirm}
            </Button>
          </>
        }
      >
        <Field
          label={DETAIL_COPY.voidReasonFieldLabel}
          htmlFor="void-reason"
          hint={DETAIL_COPY.voidReasonHint}
          error={formError ?? undefined}
        >
          <Textarea id="void-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {refusal?.action === "void" && (
          <RefusalNotice
            className="mt-3"
            error={refusal.error}
            title={isPaymentInFlight(refusal.error) ? DETAIL_COPY.voidRefusedTitle : DETAIL_COPY.refusedTitle}
          />
        )}
      </Modal>
    </section>
  );
}
